import React, { useState, useEffect, useCallback, useMemo } from "react";

import PageHeader from "Components/Pages/Header";
import Breadcrumb from "Components/Shared/Breadcrumb";
import { useHistory, useParams } from "react-router";
import { MaskCPF } from "Utils/Masks";
import { createToast } from "Utils/toastFunc";
import { Box } from "styles/globalComponents";
import { Button } from "Components/Button";
import { Brain, Search } from "lucide-react";
import { Loader } from "Components/Loader";
import { PREBUDGETSTATUS } from "Constants/PreBudget";
import { PageContainer, PageContent } from "Pages/PreBudgetConvertion/styles";
import { Product } from "Hooks/useProducts/interface";
import { Documents } from "./components/Document";
import { CartDetails } from "./components/Cart";
import { ItemsProvider } from "./Provider";
import { useItemsContext } from "./hooks/useItemsContext";
import { usePreOrderDetails } from "./hooks/usePreOrderDetails";
import { UnifiedProductModal } from "./components/UnifiedProductModal/index";
import { useIAState } from "./hooks/useIAState";
import { useProductsByIds } from "./hooks/useProductsByIds";
import { ProductWithSource, IAResponse, ProductFromAPI } from "./interface";

const ConvertPreOrderIAUnit = () => {
  const { id } = useParams<{ id: string }>();
  const [isPageBlocked, setIsPageBlocked] = useState(false);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);

  const preOderDetailsAPI = usePreOrderDetails(Number(id));

  // Hook da IA movido para o componente pai para compartilhar estado
  const {
    isProcessing,
    iaResponse,
    generateWithIA,
    clearIAState,
    getProductIdsFromIA,
  } = useIAState();

  // Hook para buscar produtos por IDs da IA
  const productsResponse = useProductsByIds(getProductIdsFromIA());

  const history = useHistory();

  const backListPage = () => history.push("/listagem-pre-orcamento/unidade");

  useEffect(() => {
    if (preOderDetailsAPI?.data?.status?.id === PREBUDGETSTATUS.canceled) {
      setIsPageBlocked(true);

      createToast(
        "error",
        "Não é possível editar este pré-orçamento, pois o mesmo foi cancelado."
      );
      return;
    }

    if (preOderDetailsAPI?.data?.budget_id) {
      setIsPageBlocked(true);
      createToast(
        "error",
        "Não é possível editar este pré-orçamento, pois o mesmo já foi convertido em orçamento."
      );
      return;
    }
  }, [preOderDetailsAPI.data]);

  if (preOderDetailsAPI.error) {
    return (
      <PageContainer className="pre-budget-conversion-ia  entry-app-content entry-budget">
        <PageContent className="content-holder page-budget">
          <Box
            display="flex"
            direction="column"
            justify="center"
            className="edit-blocked"
          >
            <h3 className="text-center">
              O pré-orçamento está bloqueado para edição no momento, pois outro
              usuário já está editando este pré-orçamento.
            </h3>

            <Button onClick={backListPage}>Voltar para listagem</Button>
          </Box>
        </PageContent>
      </PageContainer>
    );
  }

  return (
    <div className="pre-budget-conversion-ia entry-app-content entry-budget">
      <div className="page-content content-holder">
        <div className="page-heading">
          <div className="wrapper-header-content m-bottom-1 order-budget">
            <PageHeader title="Conversão pré-orçamento com IA" isBudget>
              <Breadcrumb
                list={[
                  {
                    path: "/listagem-pre-orcamento/domiciliar",
                    label: "Pré-orçamento",
                    current: false,
                  },
                  {
                    path: "#",
                    label: "Adicionar produtos",
                    current: true,
                  },
                ]}
              />
            </PageHeader>
            <div className="budget-header-user">
              <h5>Convertendo orçamento para:</h5>
              <p>{preOderDetailsAPI.data?.full_name || "Não informado"}</p>
              <p>
                {MaskCPF(preOderDetailsAPI.data?.document) || "Não informado"}{" "}
              </p>
            </div>
          </div>
        </div>

        {/* <div className="page-sidebar-style">
          <Sidebar />
        </div> */}

        <ItemsProvider
          iaResponse={iaResponse || undefined}
          productsResponse={productsResponse}
        >
          <MainContent
            isProductModalOpen={isProductModalOpen}
            setIsProductModalOpen={setIsProductModalOpen}
            files={preOderDetailsAPI.data?.files}
            isProcessing={isProcessing}
            iaResponse={iaResponse}
            generateWithIA={generateWithIA}
            clearIAState={clearIAState}
            getProductIdsFromIA={getProductIdsFromIA}
            blockInteraction={isPageBlocked}
          />

          <div className="cart-summary-style">
            <CartDetails
              blockInteraction={isPageBlocked}
              setPageBlocked={setIsPageBlocked}
              patientData={preOderDetailsAPI.data}
              iaResponse={iaResponse}
            />
          </div>
        </ItemsProvider>
      </div>
    </div>
  );
};

// Componente separado para acessar o contexto
const MainContent = ({
  isProductModalOpen,
  setIsProductModalOpen,
  files,
  isProcessing,
  iaResponse,
  generateWithIA,
  clearIAState,
  getProductIdsFromIA,
  blockInteraction,
}: {
  isProductModalOpen: boolean;
  setIsProductModalOpen: (open: boolean) => void;
  files?: { file_path: string }[];
  isProcessing: boolean;
  iaResponse: IAResponse | null;
  generateWithIA: (fileBlobs: Blob[]) => Promise<void>;
  clearIAState: () => void;
  getProductIdsFromIA: () => number[];
  blockInteraction: boolean;
}) => {
  const {
    items,
    addItem,
    fileBlobs,
    resetTrackHistory,
    initializeNewTracking,
    addManualProduct,
  } = useItemsContext();

  // Memoizar a função de seleção de produto para evitar recriações
  const handleProductSelect = useCallback(
    (product: Product) => {
      // Adicionar produto manual ao carrinho
      const productWithSource: ProductWithSource = {
        product_id: product.id,
        product_name: product.product || "Produto não identificado",
        product_code: product.sku || "",
        amount: 1,
        product_price:
          product.partners && product.partners.length > 0
            ? product.partners[0].price
            : 0,
        source: "manual",
      };

      addItem(productWithSource);

      // Adicionar ao tracking de produtos manuais
      addManualProduct(productWithSource);

      createToast("success", "Produto adicionado ao carrinho!");
    },
    [addItem, addManualProduct]
  );

  // Memoizar os IDs dos produtos da IA para evitar recálculos desnecessários
  const productIdsFromIA = useMemo(
    () => getProductIdsFromIA(),
    [getProductIdsFromIA]
  );

  // Buscar produtos por IDs
  const { products: iaProducts, isLoading: isLoadingProducts } =
    useProductsByIds(productIdsFromIA);

  // Memoizar a função de processamento de produtos da IA
  const handleProcessIAProducts = useCallback(async () => {
    if (!iaResponse || !iaProducts.length) return;

    try {
      // 1. INICIALIZAR NOVO TRACKING ANTES DE PROCESSAR PRODUTOS
      initializeNewTracking(iaResponse);

      // Criar mapeamento de produtos por ID para busca rápida
      const productsMap = new Map(
        iaProducts.map((product: ProductFromAPI) => [product.id, product])
      );

      // Processar na ordem original dos resultados da IA
      iaResponse.matching_results.resultados
        .filter((result) => result.encontrado && result.id)
        .forEach((result) => {
          if (!result.id) return;
          const iaProduct = productsMap.get(result.id as number);

          if (
            !iaProduct ||
            !iaProduct.partners ||
            iaProduct.partners.length === 0
          ) {
            return;
          }

          const productWithSource: ProductWithSource = {
            product_id: iaProduct.id,
            product_name: iaProduct.product || "Produto não identificado",
            product_code: iaProduct.sku || "",
            amount: 1,
            product_price: iaProduct.partners[0].price || 0,
            source: "ia",
            originalIAProduct: {
              nome_enviado: result.nome_original,
              nome_encontrado: result.nome_encontrado || iaProduct.product,
              id_encontrado: result.id,
            },
          };

          addItem(productWithSource);
        });

      // Limpar estado da IA após adicionar ao carrinho
      clearIAState();
    } catch (err) {
      console.log("err", err);
      createToast(
        "error",
        "Erro ao adicionar produtos ao carrinho. Tente novamente."
      );
    }
  }, [iaResponse, iaProducts, addItem, clearIAState, initializeNewTracking]);

  // Memoizar a função de geração com IA
  const handleGenerateWithIA = useCallback(async () => {
    if (!files || files.length === 0) {
      createToast("error", "Não há documentos para processar");
      return;
    }

    try {
      // Resetar o track quando "Gerar com IA" for executado
      resetTrackHistory();

      // Usar os blobs já carregados em vez de fazer novas requisições
      const fileBlobsArray = files
        .map((file) => fileBlobs[file.file_path])
        .filter(Boolean);

      if (fileBlobsArray.length === 0) {
        createToast("error", "Arquivos ainda não foram carregados");
        return;
      }

      await generateWithIA(fileBlobsArray);
    } catch (error) {
      createToast("error", "Erro ao processar documentos");
    }
  }, [files, fileBlobs, generateWithIA, resetTrackHistory]);

  // Processar produtos da IA automaticamente quando estiverem disponíveis
  // Adicionar verificação para evitar processamento duplicado
  useEffect(() => {
    if (iaResponse && iaProducts.length > 0 && !isLoadingProducts) {
      // Verificar se já foi processado para evitar duplicação
      const hasProcessed = items.some((item) => item.source === "ia");
      if (!hasProcessed) {
        handleProcessIAProducts();
      }
    }
  }, [
    iaResponse,
    iaProducts,
    isLoadingProducts,
    items,
    handleProcessIAProducts,
  ]);

  return (
    <div className="main-content-style">
      {/* Botões de ação */}
      <Box display="flex" justify="flex-end" gap="5" mb="20">
        <Button
          classButton="btn-action"
          disabled={isProcessing || items.length > 0 || blockInteraction}
          onClick={handleGenerateWithIA}
        >
          <Brain />
          {isProcessing ? "Processando..." : "Gerar com IA"}
        </Button>

        <Button
          classButton="btn-action"
          onClick={() => setIsProductModalOpen(true)}
          disabled={blockInteraction}
        >
          <Search /> Buscar produtos
        </Button>
      </Box>

      {/* Documentos */}
      <Documents files={files} />

      {/* Overlay de processamento da IA */}
      {(isProcessing || (iaResponse && isLoadingProducts)) && (
        <div className="overlay-progress-ia">
          <div className="progress">
            <Loader size={40} color="var(--color-purple)" />
            <p>Processando com IA...</p>
          </div>
        </div>
      )}

      <UnifiedProductModal
        isOpen={isProductModalOpen}
        onClose={() => setIsProductModalOpen(false)}
        mode="search"
        onProductSelect={handleProductSelect}
        multiple={false}
      />

      <style>{`
        .overlay-progress-ia {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .progress {
          background: white;
          padding: 40px;
          border-radius: 12px;
          text-align: center;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
        }

        p {
          margin: 0;
          color: var(--color-text);
          font-size: 16px;
        }
      `}</style>
    </div>
  );
};

export default ConvertPreOrderIAUnit;
